import { DefaultButton, Icon } from "@fluentui/react";
import { t } from "i18next";

import { BoardSortable, SortableRow, openHandlers } from "./boardRow";
import "./protocolTable.css";

/**
 * The protocols of one group, as the app's table one rank down.
 *
 * This is the group table again, not a different kind of surface and not a list
 * parked inside a row: the same header — sentence case, the theme's thead colour,
 * ruled with the table rule — over rows that band, rule and highlight exactly as
 * the rows above them do. Everything separating the two is a rank rather than a
 * difference in kind:
 *
 *   - tone: the group list bands in the theme's row pair, this one in the pair the
 *     app keeps for nested rows — the same two tones a prior study is shown in
 *     under the study that owns it, on the study list;
 *   - size: 13px header over 12.5px rows on 42px lines, against 16 over 14 on 60;
 *   - place: indented under the group's name and bracketed by a rail that turns
 *     under the last row, which is the connector the study list draws between a
 *     study and its priors.
 *
 * Its own component rather than a block inside the board because it is a table in
 * its own right — its own columns, its own header, its own drag context — and
 * because reading the board should not mean reading two tables at once.
 *
 * Nothing here writes. Reordering and deleting are reported to the caller, which
 * holds them as a draft until Save. A protocol the caller has marked says so by
 * `pendingDelete`: it keeps its place, struck through and out of the numbering,
 * and its delete button becomes the undo.
 *
 * A protocol under a group that is itself leaving has no undo of its own — put the
 * group back and it comes back with it — so `groupLeaving` empties the action cell
 * rather than offering a button that would answer the wrong question.
 */

/**
 * What a protocol's row says about it beyond its name: how big it hangs. The size
 * comes off the list summary, so the drawer costs no extra request.
 *
 * A cell of the row's grid rather than a strip of chips: what makes a list of
 * protocols readable is that every layout sits under the layout above it, and a
 * strip that shrinks to fit its contents lines nothing up. A protocol with no grid
 * of its own leaves its cell empty rather than closing it, so the column below
 * does not shift.
 *
 * The conditions that decide when a protocol applies are not on the row: a whole
 * condition sentence per protocol took more width than it earned on a list, and
 * the protocol editor is where they are read and changed.
 */
function ProtocolFacts({ protocol }) {
  return (
    <span className="hpv2-board-protocol-layout">
      {protocol.rows > 0 && protocol.columns > 0 && (
        <span className="hpv2-board-fact is-strong">
          <Icon iconName="GridViewSmall" />
          {t("hanging-protocol.view-count", { rows: protocol.rows, columns: protocol.columns })}
        </span>
      )}
    </span>
  );
}

export default function ProtocolTable({
  group,
  protocols = [],
  readOnly = false,
  groupLeaving = false,
  onOpenProtocol,
  onDeleteProtocol,
  onRestoreProtocol,
  onReorderProtocols,
}) {
  // Numbered around the rows that are leaving, for the reason the groups are: they
  // are out of the matching order already, and renumbering twice — once now, once
  // on Save — would read as the drag nobody made.
  const positions = new Map();
  protocols
    .filter((protocol) => !protocol.pendingDelete)
    .forEach((protocol, index) => positions.set(protocol.id, index + 1));

  const onDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) onReorderProtocols?.(group.id, active.id, over.id);
  };

  return (
    <div className="hpv2-board-drawer">
      <div className="hpv2-board-protocol-table">
        {/* Column names, once per table. The rows carry unlike things — which
            protocol and how big it hangs — and at this size neither of them says
            which it is on its own. */}
        <div className="hpv2-board-protocol-head">
          <span />
          <span title={t("hanging-protocol.first-match-wins")}>#</span>
          <span>{t("hanging-protocol.protocol")}</span>
          <span>{t("hanging-protocol.layout")}</span>
          {/* Nothing here to do to a protocol nobody may change, so no column for it:
              the board above drops its own Action column for the same reason. */}
          {!readOnly && <span>{t("supervisor.action")}</span>}
        </div>

        <BoardSortable ids={protocols.map((protocol) => protocol.id)} onDragEnd={onDragEnd}>
          <ul className="hpv2-board-protocols">
            {protocols.map((protocol) => {
              const openProtocol = () => onOpenProtocol?.(group, protocol);
              const name = protocol.name || t("hanging-protocol.new-protocol");
              // A protocol under a group that is leaving is leaving with it, so the
              // table says so on every row rather than only on the line above them.
              const leaving = groupLeaving || Boolean(protocol.pendingDelete);

              return (
                <SortableRow
                  key={protocol.id}
                  id={protocol.id}
                  disabled={readOnly || leaving}
                  className={`hpv2-board-protocol ${leaving ? "is-pending-delete" : ""}`}
                  gripLabel={`${t("hanging-protocol.drag-to-reorder")} ${name}`}
                  gripClassName="is-nested"
                >
                  {(grip) => (
                    <div
                      className="hpv2-board-protocol-row"
                      data-testid={`hpv2-protocol-${protocol.id}`}
                      role="button"
                      tabIndex={0}
                      title={t("hanging-protocol.view-protocol")}
                      aria-label={`${t("hanging-protocol.view-protocol")} ${name}`}
                      {...openHandlers(openProtocol)}
                    >
                      {grip}

                      <span
                        className="hpv2-board-protocol-order"
                        title={t("hanging-protocol.first-match-wins")}
                      >
                        {positions.get(protocol.id) ?? "—"}
                      </span>

                      {/* Name over description, not beside it: side by side the
                          description pushed everything after it out of column, and
                          the two are one field. */}
                      <span className="hpv2-board-protocol-main">
                        <span className="hpv2-board-protocol-name">{name}</span>
                        {protocol.description && (
                          <span className="hpv2-board-protocol-desc" title={protocol.description}>
                            {protocol.description}
                          </span>
                        )}
                      </span>

                      <ProtocolFacts protocol={protocol} />

                      {/* Opening the protocol is the row's own press, so what is left
                          in the cell the labels head is the one act the wizard cannot
                          do to itself — of which a reader has none, so their rows carry
                          no such cell at all. */}
                      {!readOnly && (
                        <span className="hpv2-board-protocol-actions">
                          {!groupLeaving && (
                            <DefaultButton
                              className={leaving ? "hpv2-board-restore" : "hpv2-board-delete"}
                              text={t(
                                leaving ? "hanging-protocol.undo-delete" : "hanging-protocol.delete-action"
                              )}
                              ariaLabel={`${t(
                                leaving ? "hanging-protocol.undo-delete" : "hanging-protocol.delete"
                              )} ${name}`}
                              data-testid={leaving ? `hpv2-protocol-restore-${protocol.id}` : undefined}
                              onClick={() =>
                                leaving
                                  ? onRestoreProtocol?.(group, protocol)
                                  : onDeleteProtocol?.(group, protocol)
                              }
                            />
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </SortableRow>
              );
            })}
          </ul>
        </BoardSortable>
      </div>
    </div>
  );
}
